import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Car, Bike, ShieldCheck, Users, Award, ArrowRight, Pause, Play, Calendar, Quote, TrendingUp,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { CERTS } from "../lib/content";
import { useContent } from "../context/ContentContext";
import { formatDate as fmt } from "../lib/date";
import api from "../lib/api";

const HERO_SLIDES = [
  { url: "https://customer-assets-39nsmqrw.emergentagent.net/job_rospa-wm-portal/artifacts/db9qeg0a_image.png", label: "RoSPA advanced riders on the road" },
  { url: "https://images.unsplash.com/photo-1558981806-ec527fa84c39?auto=format&fit=crop&q=85&w=1600", label: "Advanced riders on the road" },
  { url: "https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?auto=format&fit=crop&q=85&w=1600", label: "Advanced drivers in training" },
  { url: "https://images.pexels.com/photos/28887409/pexels-photo-28887409.jpeg?auto=format&fit=crop&q=85&w=1600", label: "Group rides and social" },
];

const TESTIMONIALS = [
  { name: "Andrew Pinnington", role: "RoSPA Gold Rider & NHS Blood Biker", quote: "The advanced certification allowed me to join Blood Bikes delivering urgent medical supplies. A hugely rewarding way to put those skills to good use." },
  { name: "Richard Long", role: "Advanced Rider Member", quote: "Returning to biking after 30 years, advanced training was my best chance of seeing a few more summers. The group is friendly and affordable." },
  { name: "Paul Timmins", role: "Group Ride Leader & Tutor", quote: "I achieved my RoSPA Gold and now help train new associates and organise group rides. Great community support every weekend." },
];

function useReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(mq.matches);
    const h = (e) => setReduced(e.matches);
    mq.addEventListener("change", h);
    return () => mq.removeEventListener("change", h);
  }, []);
  return reduced;
}

export default function Home() {
  const { t, content } = useContent();
  const reduced = useReducedMotion();
  const resolveImg = (v) => (v && v.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${v}` : v);
  const managedSlides = content["img.home.slides"];
  const heroSlides = (Array.isArray(managedSlides) && managedSlides.length ? managedSlides : HERO_SLIDES.map((s, i) => t(`img.home.slide${i}`, s.url))).filter(Boolean);
  const [slide, setSlide] = useState(0);
  const [paused, setPaused] = useState(false);
  const [events, setEvents] = useState([]);
  const timer = useRef(null);

  useEffect(() => {
    api.get("/events").then(({ data }) => setEvents(data.slice(0, 3))).catch(() => {});
  }, []);

  useEffect(() => {
    if (reduced || paused) return;
    timer.current = setInterval(() => setSlide((s) => (s + 1) % heroSlides.length), 5500);
    return () => clearInterval(timer.current);
  }, [reduced, paused]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[88vh] flex items-center overflow-hidden bg-[#0F2744]">
        {heroSlides.map((src, i) => (
          <div
            key={i}
            aria-hidden={i !== slide}
            className="absolute inset-0 transition-opacity duration-1000"
            style={{ opacity: i === slide ? 1 : 0 }}
          >
            <img src={resolveImg(src)} alt="" loading={i === 0 ? "eager" : "lazy"} className={`w-full h-full object-cover ${i === slide && heroSlides.length > 1 ? "ken-burns-active" : ""}`} />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0F2744] via-[#0F2744]/85 to-[#0F2744]/40" />
          </div>
        ))}

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 w-full">
          <div className="max-w-2xl hero-fade">
            <span className="eyebrow">{t("home.hero.eyebrow", "Advanced drivers & riders · since 1999")}</span>
            <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-[1.05] tracking-tight">
              {t("home.hero.titleLead", "Raise your roadcraft.")} <span className="text-amber-400">{t("home.hero.titleAccent", "Stay safer.")}</span> {t("home.hero.titleTail", "Enjoy the road.")}
            </h1>
            <p className="mt-6 text-lg text-slate-200 leading-relaxed max-w-xl">
              {t("home.hero.subtitle", "Friendly, volunteer-led advanced training for West Midlands riders and drivers — whatever your current ability. Work towards a nationally recognised RoSPA advanced test.")}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <Button asChild size="lg" className="bg-amber-500 hover:bg-amber-600 text-[#0F2744] font-bold h-14 px-7 text-base">
                <Link to="/riders" data-testid="hero-riders-btn"><Bike className="h-5 w-5 mr-2" /> {t("home.hero.ctaRide", "Improve my riding")}</Link>
              </Button>
              <Button asChild size="lg" className="bg-blue-600 hover:bg-blue-700 text-white font-semibold h-14 px-7 text-base">
                <Link to="/drivers" data-testid="hero-drivers-btn"><Car className="h-5 w-5 mr-2" /> {t("home.hero.ctaDrive", "Improve my driving")}</Link>
              </Button>
            </div>
          </div>
        </div>

        <div className="absolute bottom-6 right-6 flex items-center gap-3 z-10">
          <div className="flex gap-1.5">
            {heroSlides.map((_, i) => (
              <button
                key={i}
                onClick={() => setSlide(i)}
                aria-label={`Show slide ${i + 1}`}
                data-testid={`hero-dot-${i}`}
                className={`h-2 rounded-full transition-all ${i === slide ? "w-8 bg-amber-400" : "w-2 bg-white/50"}`}
              />
            ))}
          </div>
          {!reduced && (
            <button
              onClick={() => setPaused((p) => !p)}
              aria-label={paused ? "Play slideshow" : "Pause slideshow"}
              data-testid="hero-pause-btn"
              className="h-9 w-9 rounded-full bg-white/15 backdrop-blur flex items-center justify-center text-white hover:bg-white/25"
            >
              {paused ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
            </button>
          )}
        </div>
      </section>

      {/* STATS RIBBON */}
      <section className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { icon: ShieldCheck, n: "25+", l: "Years supporting road safety" },
            { icon: Users, n: "Volunteer", l: "Qualified advanced tutors" },
            { icon: Award, n: "Gold·Silver·Bronze", l: "RoSPA test grades" },
            { icon: TrendingUp, n: "All levels", l: "All members welcome" },
          ].map((s, i) => (
            <div key={i} className="flex items-center gap-3" data-testid={`stat-${i}`}>
              <div className="h-11 w-11 rounded-xl bg-[#0F2744]/5 flex items-center justify-center shrink-0">
                <s.icon className="h-6 w-6 text-[#0F2744]" />
              </div>
              <div>
                <div className="font-heading font-bold text-[#0F2744] leading-tight">{s.n}</div>
                <div className="text-xs text-slate-500">{s.l}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* PATHWAYS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="eyebrow">{t("home.pathways.eyebrow", "Choose your path")}</span>
          <h2 className="mt-3 text-3xl sm:text-4xl font-bold text-[#0F2744]">{t("home.pathways.title", "Two journeys, one friendly club")}</h2>
          <p className="mt-4 text-slate-600">{t("home.pathways.subtitle", "Whether you're on two wheels or behind the wheel, we'll match you with a local tutor and guide you towards advanced standard.")}</p>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          <PathCard to="/riders" testid="path-rider" icon={Bike} title={t("home.card.rider.title", "Advanced Riders")} accent="#F59E0B" bg="#FFFBEB"
            img={t("img.home.card.rider", "https://customer-assets-39nsmqrw.emergentagent.net/job_rospa-wm-portal/artifacts/y77k4wfe_image.png")}
            text={t("home.card.rider.text", "Sharper roadcraft, greater confidence and a brilliant community of like-minded riders.")} />
          <PathCard to="/drivers" testid="path-driver" icon={Car} title={t("home.card.driver.title", "Advanced Drivers")} accent="#2563EB" bg="#EFF6FF"
            img={t("img.home.card.driver", "https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?auto=format&fit=crop&q=85&w=800")}
            text={t("home.card.driver.text", "Smoother, safer, more confident driving on every road. Coaching in your own car, at your own pace.")} />
        </div>
      </section>

      {/* CERTIFICATION PREVIEW */}
      <section className="bg-[#0F2744] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="eyebrow">Nationally recognised</span>
            <h2 className="mt-3 text-3xl sm:text-4xl font-bold text-white">The RoSPA advanced test</h2>
            <p className="mt-4 text-slate-300">Graded Bronze, Silver or Gold, with a re-test every three years to keep standards high.</p>
          </div>
          <div className="grid sm:grid-cols-3 gap-6">
            {CERTS.map((c) => (
              <div key={c.tier} className="rounded-2xl bg-white/5 border border-white/10 p-6 hover:border-amber-500/50 transition-colors" data-testid={`cert-${c.tier.toLowerCase()}`}>
                <div className="h-12 w-12 rounded-xl flex items-center justify-center mb-4" style={{ background: c.color }}>
                  <Award className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{c.tier}</h3>
                <p className="text-sm text-slate-300 leading-relaxed">{c.summary}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Button asChild variant="outline" className="border-amber-500 text-amber-400 hover:bg-amber-500 hover:text-[#0F2744]">
              <Link to="/certification" data-testid="cert-learn-btn">Learn about the test <ArrowRight className="h-4 w-4 ml-2" /></Link>
            </Button>
          </div>
        </div>
      </section>

      {/* EVENTS PREVIEW */}
      {events.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
            <div>
              <span className="eyebrow">What's on</span>
              <h2 className="mt-3 text-3xl sm:text-4xl font-bold text-[#0F2744]">Upcoming events</h2>
            </div>
            <Button asChild variant="ghost" className="text-blue-600 hover:text-blue-700">
              <Link to="/events" data-testid="events-all-btn">See all events <ArrowRight className="h-4 w-4 ml-1" /></Link>
            </Button>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {events.map((e) => (
              <Link key={e.id} to="/events" data-testid={`home-event-${e.id}`} className="group rounded-2xl overflow-hidden bg-white border border-slate-200 hover:shadow-xl hover:-translate-y-1 transition-all">
                <div className="h-44 overflow-hidden">
                  <img src={e.image_url} alt={e.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-5">
                  <span className="text-xs font-semibold text-amber-600 uppercase tracking-wide">{e.category}</span>
                  <h3 className="mt-2 font-bold text-[#0F2744] leading-snug">{e.title}</h3>
                  <div className="mt-3 flex items-center gap-2 text-sm text-slate-500">
                    <Calendar className="h-4 w-4" /> {fmt(e.date)} {e.time && `· ${e.time}`}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* TESTIMONIALS */}
      <section className="bg-slate-100 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="eyebrow">Real members</span>
            <h2 className="mt-3 text-3xl sm:text-4xl font-bold text-[#0F2744]">Their journey, their words</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className="rounded-2xl bg-white p-7 border border-slate-200 shadow-sm" data-testid={`testimonial-${i}`}>
                <Quote className="h-8 w-8 text-amber-400 mb-4" />
                <p className="text-slate-700 leading-relaxed">"{t.quote}"</p>
                <div className="mt-5 pt-5 border-t border-slate-100">
                  <div className="font-bold text-[#0F2744]">{t.name}</div>
                  <div className="text-sm text-slate-500">{t.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden bg-[#0F2744]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white">{t("home.cta.title", "Ready to raise your standard?")}</h2>
          <p className="mt-4 text-lg text-slate-300 max-w-2xl mx-auto">{t("home.cta.subtitle", "Applying takes a couple of minutes. We'll be in touch to arrange your first steps with a local volunteer tutor.")}</p>
          <Button asChild size="lg" className="mt-8 bg-amber-500 hover:bg-amber-600 text-[#0F2744] font-bold h-14 px-10 text-base">
            <Link to="/join" data-testid="cta-join-btn">{t("home.cta.button", "Start your application")}</Link>
          </Button>
        </div>
      </section>
    </div>
  );
}

function PathCard({ to, testid, icon: Icon, title, accent, bg, img, text }) {
  return (
    <Link to={to} data-testid={testid} className="group relative rounded-3xl overflow-hidden border border-slate-200 hover:shadow-2xl transition-all duration-300">
      <div className="h-56 overflow-hidden">
        <img src={img} alt={title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
      </div>
      <div className="p-7" style={{ background: bg }}>
        <div className="h-12 w-12 rounded-xl flex items-center justify-center mb-4" style={{ background: accent }}>
          <Icon className="h-7 w-7 text-white" />
        </div>
        <h3 className="text-2xl font-bold text-[#0F2744]">{title}</h3>
        <p className="mt-2 text-slate-600">{text}</p>
        <div className="mt-4 inline-flex items-center gap-2 font-semibold" style={{ color: accent }}>
          Explore the pathway <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
        </div>
      </div>
    </Link>
  );
}
